import { debugLog } from "../utils/debugLog.js";
import { useState, useEffect, Fragment, useRef, useContext } from "react";
import AutomationDirectory from "./AutomationDirectory";
import { useUser } from "@clerk/clerk-react";
import { PlayIcon } from "@heroicons/react/24/solid";
import { Dialog, Transition } from "@headlessui/react";
import {
  XMarkIcon,
  QuestionMarkCircleIcon,
  ChatBubbleLeftRightIcon,
  BellIcon,
  SunIcon,
  MoonIcon,
  ClipboardDocumentListIcon,
  PlusIcon,
  MapIcon,
  UserIcon,
  TrophyIcon,
  CubeTransparentIcon,
  ArrowPathRoundedSquareIcon,
  ArrowPathIcon,
  FingerPrintIcon,
  QueueListIcon,
  PauseIcon,
  EllipsisVerticalIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { ReactTyped as Typed } from "react-typed";
import Airtable from "airtable";
import Notification from "../Notification";
import { SparklesIcon, BoltIcon } from "@heroicons/react/24/outline";
import { Link, useLocation } from "react-router-dom";
import FilterButton from "./FilterButton";
import { BentoCard } from "./BentoCard";
import { LogoTimeline } from "./LogoTimeline";
import IntegrationsTab from "./IntegrationsTab";
import IntegrationCard from "./IntegrationCard";
import { iconMap } from "./iconMap";
import { AppContext } from "../AppContext.js";
import Automation from "../Automation.js";
import AutomationContext from "../AutomationContext.js";

const base = new Airtable({
  apiKey: process.env.REACT_APP_AIR_KEY,
}).base(process.env.REACT_APP_AIR_BASEID);

// Tooltip — absolute to the left; FAB stack uses min-width so overflow-y doesn’t clip labels
const Tooltip = ({ text, children }) => {
  return (
    <div className="group/tooltip relative flex shrink-0 items-center justify-end">
      {children}
      <span className="pointer-events-none absolute right-full top-1/2 z-[200] mr-2 -translate-y-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover/tooltip:opacity-100">
        {text}
      </span>
    </div>
  );
};

// Dark Mode Toggle Component
const DarkModeToggle = ({ isDarkMode, setIsDarkMode }) => {
  return (
    <Tooltip text={isDarkMode ? "Light mode" : "Dark mode"}>
      <button
        onClick={() => setIsDarkMode(!isDarkMode)}
        className="p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center"
      >
        {isDarkMode ? (
          <SunIcon className="h-6 w-6 text-yellow-500 group-hover:scale-110 transition-transform duration-200" />
        ) : (
          <MoonIcon className="h-6 w-6 text-gray-600 group-hover:scale-110 transition-transform duration-200" />
        )}
      </button>
    </Tooltip>
  );
};


const GuideModal = ({ isOpen, setIsOpen }) => {
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setIsOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-7xl sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                {/* <div className="prose prose-lg max-w-none mb-12">
                  <h1 className="text-4xl font-bold mb-8 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                    🚀 The Magic of Automation: A Revolutionary Guide
                  </h1>
                  
                  <div className="space-y-8">
                    <section>
                      <h2 className="text-2xl font-bold">The Vision</h2>
                      <p className="text-gray-600">
                        Imagine a world where your boring, repetitive problems vanish in clicks.
                        We <strong>already</strong> live in a world where AI can take away your headache & stress.
                        <br /><br />
                        Let us take the boring+repetitive out of your work, so you can focus on doing what you love.
                        <br /><br />
                        Automating your work unlocks more than just time - it's about freedom & actualization.
                      </p>
                    </section>
                  </div>
                </div> */}
                <div className="bg-white py-4">
                  <div className="mx-auto max-w-3xl px-6 lg:max-w-7xl lg:px-4">
                    <p className="mt-2 max-w-4xl text-pretty text-4xl font-semibold tracking-tight text-gray-950 sm:text-5xl">
                      The Magic of AI Automation
                      <span className="block mt-4 text-2xl text-gray-400 font-normal">
                        Everything you need to automate your boring + repetitive
                        work
                      </span>
                    </p>
                    <div className="mt-4 grid grid-cols-1 gap-4 sm:mt-8 lg:grid-cols-6 lg:grid-rows-2 lg:gap-6">
                      <div className="relative lg:col-span-3">
                        <BentoCard
                          eyebrow="Smart Search"
                          title="Intelligent Discovery"
                          description="Describe what you want to automate in plain English to match you with the perfect automation solutions."
                          graphic={
                            <video
                              autoPlay
                              muted
                              loop
                              playsInline
                              className="h-80 w-full object-cover object-left"
                            >
                              <source
                                src="https://cdn.dribbble.com/userupload/10156529/file/original-bc4415c45f504ffac6668e1b3fa8d070.mp4"
                                type="video/mp4"
                              />
                            </video>
                          }
                          className="max-lg:rounded-t-[2rem] lg:rounded-tl-[2rem] h-full"
                        />
                      </div>
                      <div className="relative lg:col-span-3">
                        <BentoCard
                          eyebrow="Value Metrics"
                          title="Clear ROI & Impact"
                          description="See the exact time and money saved with each automation. Real-time metrics show monthly cost savings and efficiency gains."
                          graphic={
                            <img
                              alt="ROI and metrics visualization"
                              src="https://cdn.dribbble.com/userupload/16355619/file/original-109fa73f8369e2c7d80c952b64af2a93.jpg?resize=1024x768&vertical=center"
                              className="h-80 w-full object-cover object-left lg:object-right"
                            />
                          }
                          className="lg:rounded-tr-[2rem] h-full"
                        />
                      </div>
                      <div className="relative lg:col-span-2">
                        <BentoCard
                          eyebrow="Quick Setup"
                          title="2 - 14 Day Delivery"
                          description="We get your automation up and running in days, not weeks. Simple requirements form and rapid deployment process."
                          graphic={
                            <img
                              alt="Quick setup process"
                              src="https://cdn.dribbble.com/userupload/18075232/file/original-e9aff1276d2ac41e257692c2e31dd178.png?resize=1024x768&vertical=center"
                              className="h-80 w-full object-cover object-left"
                            />
                          }
                          className="lg:rounded-bl-[2rem] h-full"
                        />
                      </div>
                      <div className="relative lg:col-span-2">
                        <BentoCard
                          dark
                          eyebrow="Integrations"
                          title="Meet you where you are"
                          description="With ample integrations, we'll find the best way to connect with your stack."
                          graphic={<LogoTimeline />}
                          className="z-10 overflow-visible! h-full"
                        />
                      </div>
                      <div className="relative lg:col-span-2">
                        <BentoCard
                          eyebrow="Risk-Free Trial"
                          title="30-Day Guarantee"
                          description="Try automations risk-free, 30-day money-back guarantee. Early access members get a 14-day free trial."
                          graphic={
                            <img
                              alt="Money-back guarantee"
                              src="https://cdn.dribbble.com/users/458944/screenshots/5058685/30day-01_4x.png?resize=1000x750&vertical=center"
                              className="h-80 w-full object-cover"
                            />
                          }
                          className="max-lg:rounded-b-[2rem] lg:rounded-br-[2rem] h-full"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

// Function to get integrations (same as in Settings.js)
const getIntegrations = () => [
  {
    key: 'quickbooks',
    name: 'Quickbooks',
    logoUrl: iconMap.quickbooks,
    requiresAuth: true
  },
  {
    key: 'hubspot',
    name: 'Hubspot',
    logoUrl: iconMap.hubspot,
    requiresAuth: false
  },
  {
    key: 'stripe',
    name: 'Stripe',
    logoUrl: iconMap.stripe,
    requiresAuth: false
  },
  {
    key: 'google-analytics',
    name: 'Google Analytics',
    logoUrl: 'https://logo.clearbit.com/analytics.google.com',
    requiresAuth: false
  },
];

const IntegrationsModal = ({ isOpen, setIsOpen, user, setNotification }) => {
  const [upvotedIntegrations, setUpvotedIntegrations] = useState(new Set());
  const [upvoteCounts, setUpvoteCounts] = useState({});
  const userId = user?.unsafeMetadata?.airtableId || user?.id;
  const userEmail = user?.primaryEmailAddress?.emailAddress;

  // Load upvoted integrations and counts
  useEffect(() => {
    if (!isOpen) return;
    
    const loadUpvotes = async () => {
      try {
        const url = userEmail 
          ? `/api/integration-upvotes?userEmail=${encodeURIComponent(userEmail)}`
          : '/api/integration-upvotes';
        
        const response = await fetch(url);
        const data = await response.json();

        if (response.ok && data.success) {
          setUpvoteCounts(data.upvoteCounts || {});
          
          if (data.userUpvotes) {
            setUpvotedIntegrations(new Set(data.userUpvotes));
          }
        } else {
          console.error("Error loading upvotes:", data.error || data.details);
        }
      } catch (error) {
        console.error("Error loading upvotes:", error);
      }
    };

    loadUpvotes();
  }, [isOpen, userId, userEmail]);

  const handleUpvote = async (integrationName) => {
    if (!userId || !userEmail) {
      setNotification({
        showNotif: true,
        contents: {
          type: "error",
          heading: "Error",
          message: "Please sign in to upvote integrations",
        },
      });
      return;
    }

    const isUpvoted = upvotedIntegrations.has(integrationName);

    try {
      const response = await fetch('/api/integration-upvote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          userEmail,
          integrationName,
          action: isUpvoted ? 'remove' : 'add',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to update upvote');
      }

      // Update local state based on response
      if (isUpvoted) {
        // Remove upvote
        setUpvotedIntegrations(prev => {
          const newSet = new Set(prev);
          newSet.delete(integrationName);
          return newSet;
        });
      } else {
        // Add upvote
        setUpvotedIntegrations(prev => new Set([...prev, integrationName]));
      }

      // Update count from server response
      setUpvoteCounts(prev => ({
        ...prev,
        [integrationName]: data.upvoteCount || 0,
      }));
    } catch (error) {
      console.error("Error toggling upvote:", error);
      setNotification({
        showNotif: true,
        contents: {
          type: "error",
          heading: "Error",
          message: error.message || "Failed to update upvote. Please try again.",
        },
      });
    }
  };

  const handleInstall = (integration) => {
    // For now, just show a notification
    // In the future, this could open a sidebar similar to Settings
    setNotification({
      showNotif: true,
      contents: {
        type: "info",
        heading: "Integration Installation",
        message: `To install ${integration.name}, please go to Settings > Integrations`,
      },
    });
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setIsOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-7xl sm:p-6 max-h-[90vh] overflow-y-auto">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <Dialog.Title
                  as="h3"
                  className="text-2xl font-semibold leading-6 text-gray-900 mb-6"
                >
                  Integrations
                </Dialog.Title>

                <div className="mt-4">
                  <IntegrationsTab
                    getIntegrations={getIntegrations}
                    IntegrationCard={IntegrationCard}
                    handleInstall={handleInstall}
                    onUpvote={handleUpvote}
                    upvotedIntegrations={upvotedIntegrations}
                    upvoteCounts={upvoteCounts}
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

const AutomateForm = ({ isOpen, setIsOpen }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    tools: "",
    expectedBenefit: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Create record in automationRequests table
      await base("automationRequests").create([
        {
          fields: {
            AutomationDescription: formData.title,
            Requirements: formData.description,
            TechnologiesUsed: formData.tools,
            Savings: formData.expectedBenefit,
            Status: "Requested",
            Date: new Date().toISOString().split("T")[0],
            Type: "Custom",
          },
        },
      ]);

      // Create record in automationsAIGrowthUpgrades table with additional fields
      await base("automationsAIGrowthUpgrades").create([
        {
          fields: {
            Title: formData.title,
            IncludedFeatures: formData.description,
            Tools: formData.tools,
            Benefits: formData.expectedBenefit, // Benefits appears to be an array in the schema
            Stage: "requested",
            Type: "Custom",
            SetupPrice: 499, // Default setup price
            MonthlyPrice: 99, // Default monthly price
            TurnaroundDays: 3, // Default turnaround days
            // LastUpdated: new Date().toISOString(),
            // LastModified: new Date().toISOString(),
            requirements: JSON.stringify({
              fromClient: [
                {
                  name: "Project Requirements",
                  example: formData.description,
                },
              ],
            }),
            WorkflowDetails: JSON.stringify({
              stages: [
                {
                  name: "Setup",
                  duration: "3-5 days",
                  description: "Initial configuration and setup",
                },
              ],
            }),
          },
        },
      ]);

      setIsOpen(false);
      // Optionally add success toast/notification
    } catch (error) {
      console.error("Error creating automation request:", error);
      // Optionally add error toast/notification
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setIsOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="mt-3 text-center sm:mt-5">
                  <Dialog.Title
                    as="h3"
                    className="text-base font-semibold leading-6 text-gray-900"
                  >
                    Request New Automation
                  </Dialog.Title>

                  <form
                    onSubmit={handleSubmit}
                    className="mt-6 space-y-6 text-left"
                  >
                    <div>
                      <label
                        htmlFor="title"
                        className="block text-sm font-medium text-gray-700"
                      >
                        What would you like to automate?
                      </label>
                      <input
                        type="text"
                        name="title"
                        id="title"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-stone-500 focus:ring-stone-500 sm:text-sm"
                        value={formData.title}
                        onChange={(e) =>
                          setFormData({ ...formData, title: e.target.value })
                        }
                        required
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="description"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Describe the process in detail
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        rows={4}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-stone-500 focus:ring-stone-500 sm:text-sm"
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        required
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="tools"
                        className="block text-sm font-medium text-gray-700"
                      >
                        What tools/software do you use for this process?
                      </label>
                      <input
                        type="text"
                        name="tools"
                        id="tools"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-stone-500 focus:ring-stone-500 sm:text-sm"
                        value={formData.tools}
                        onChange={(e) =>
                          setFormData({ ...formData, tools: e.target.value })
                        }
                        required
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="expectedBenefit"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Expected time/money saved per month
                      </label>
                      <input
                        type="text"
                        name="expectedBenefit"
                        id="expectedBenefit"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-stone-500 focus:ring-stone-500 sm:text-sm"
                        value={formData.expectedBenefit}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            expectedBenefit: e.target.value,
                          })
                        }
                        required
                      />
                    </div>

                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                      <button
                        type="submit"
                        className="inline-flex w-full justify-center rounded-md bg-stone-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-stone-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stone-600 sm:col-start-2"
                      >
                        Submit Request
                      </button>
                      <button
                        type="button"
                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                        onClick={() => setIsOpen(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

const NotificationsDropdown = ({
  isOpen,
  setIsOpen,
  showVideoCard,
  setShowVideoCard,
  showBottomNotification,
  setShowBottomNotification,
  admin,
}) => {
  return (
    <div className="relative">
      {/* Notification Bell Button */}
      <Tooltip text="Notifications">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center relative ${isOpen ? "ring-2 ring-stone-600" : ""}`}
        >
          <BellIcon className="h-6 w-6 text-stone-600 group-hover:scale-110 transition-transform duration-200" />
          {!showVideoCard ||
          !showBottomNotification ||
          (admin && !showBottomNotification) ? (
            <span className="absolute top-0 right-0 h-3 w-3 bg-red-500 rounded-full"></span>
          ) : null}
        </button>
      </Tooltip>

      {/* Dropdown Content */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-[200]">
          <div className="p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Notifications
            </h3>

            {/* Video Card Notification */}
            {!showVideoCard && (
              <div
                className="mb-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                onClick={() => {
                  setShowVideoCard(true);
                  setIsOpen(false);
                }}
              >
                <div className="flex items-center gap-3">
                  <PlayIcon className="h-5 w-5 text-stone-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Watch Demo Video
                    </p>
                    <p className="text-xs text-gray-500">
                      See how automations transform your company
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Unlimited Access Notification */}
            {!showBottomNotification && (
              <div
                className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                onClick={() => {
                  setShowBottomNotification(true);
                  setIsOpen(false);
                }}
              >
                <div className="flex items-center gap-3">
                  <SparklesIcon className="h-5 w-5 text-stone-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Unlimited Automation Access
                    </p>
                    <p className="text-xs text-gray-500">
                      Learn about our unlimited automation plan
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Automation Requests Link - Only show for admin users */}
            {admin && !showBottomNotification && (
              <>
                <Link to="/automation-requests" className="block">
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
                    <div className="flex items-center gap-3">
                      <BoltIcon className="h-6 w-6 text-stone-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Automation Requests
                        </p>
                        <p className="text-xs text-gray-500">
                          Earn up to $300/hr or $10,000 for each automation you
                          wire up
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>

                <Link to="/upgrader-subscriptions" className="block">
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
                    <div className="flex items-center gap-3">
                      <BellIcon className="h-6 w-6 text-stone-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          My Subscriptions
                        </p>
                        <p className="text-xs text-gray-500">
                          Manage the AI & Automations you're subscribed to receive notifications for.
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
              </>
            )}

            {/* Show when all notifications are viewed */}
            {showVideoCard && showBottomNotification && !admin && (
              <p className="text-sm text-gray-500 text-center">
                No new notifications
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Header = ({
  runTour,
  setRunTour,
  admin,
  setNotification,
  isDarkMode,
  showAutomateForm,
  setShowAutomateForm,
  showGuide,
  setShowGuide,
  typedKey,
  setTypedKey,
}) => {
  return (
    <header className="flex max-lg:flex-col justify-center p-2">
      <div className="space-y-4 relative w-full">
        <div className="flex items-center justify-center gap-4 w-full">
          <h1
            className={`group text-2xl md:text-3xl font-extrabold transition-colors duration-300 text-center w-full ${isDarkMode ? "text-white" : "text-gray-900"}`}
            style={{ fontFamily: "Recoleta, 'Crimson Pro', serif" }}
            onMouseEnter={() => setTypedKey(prev => prev + 1)}
          >
            AI My{" "}
            <Typed
              key={typedKey}
              strings={[
                "Work",
                "Boring & Repetitive",
                "Data Entry",
                "Technical Set Up",
                "Marketing",
                "HR",
                "Finance",
                "Onboarding",
                "Customer Success",
                "Project Mgmt",
                "Sales",
                "Recruiting",
                "Accounting",
                "Work",
              ]}
              typeSpeed={40}
              backSpeed={50}
              loop={true}
              backDelay={1000}
              showCursor={true}
              cursorChar="|"
              smartBackspace={true}
            />
          </h1>
        </div>
      </div>

      <GuideModal isOpen={showGuide} setIsOpen={setShowGuide} />

      {/* Mobile navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 lg:hidden z-40">
        <div className="flex justify-around p-4">
          <button
            onClick={() => setShowAutomateForm(true)}
            className="flex flex-col items-center px-3 py-2"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            <span className="text-xs mt-1">Automate</span>
          </button>

          <button
            onClick={() => setShowGuide(true)}
            className="flex flex-col items-center px-3 py-2"
          >
            <PlayIcon className="h-6 w-6 text-stone-700" />
            <span className="text-xs mt-1">Guide</span>
          </button>

          <button
            onClick={() => setRunTour(true)}
            className="flex flex-col items-center px-3 py-2"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-xs mt-1">Tour</span>
          </button>
        </div>
      </div>
    </header>
  );
};

// Clipboard List Component
const ClipboardList = ({ 
  showClipboardList, 
  setShowClipboardList, 
  prioritizedIds, 
  allCards, 
  onRemove 
}) => {
  // Helper to get prioritized cards in order
  const getPrioritizedCards = (prioritizedIds, allCards) =>
    prioritizedIds
      .map((id) => allCards.find((card) => card.id === id))
      .filter(Boolean);

  const cards = getPrioritizedCards(prioritizedIds, allCards);
  
  // Don't render anything if there are no cards
  if (cards.length === 0) return null;
  
  return (
    <>
      {/* Toggle button - always visible when there are cards */}
      <Tooltip text={showClipboardList ? "Hide My Queue" : "My Queue"}>
        <button
          onClick={() => setShowClipboardList(!showClipboardList)}
          className={`bg-white hover:bg-gray-50 text-black p-3 rounded-full shadow-lg transition-all duration-200 hover:shadow-xl relative group ${
            showClipboardList ? "ring-2 ring-blue-500" : ""
          }`}
        >
          <QueueListIcon className="w-6 h-6 text-stone-600 group-hover:scale-110 transition-transform duration-200" />
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
            {cards.length}
          </span>
        </button>
      </Tooltip>
      
      {/* Main list panel */}
      {showClipboardList && (
        <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-4 w-80 max-h-96 overflow-y-auto z-[200]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">My Queue</h3>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">{cards.length} {cards.length === 1 ? 'item' : 'items'}</span>
              <button
                onClick={() => setShowClipboardList(false)}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                title="Hide My List"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {cards.map((card, index) => (
              <div
                key={card.id}
                className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {card.title}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {card.description}
                  </p>
                </div>
                <button
                  onClick={() => onRemove(card.id)}
                  className="ml-2 p-1 text-gray-400 hover:text-red-500"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default function AutomateExperience({
  company: companyProp,
  embeddedInLayout = false,
}) {
  const appContext = useContext(AppContext);
  const company = companyProp || appContext?.company;
  const location = useLocation();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [admin, setAdmin] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [isHoveringActionButtons, setIsHoveringActionButtons] = useState(false);
  const [actionBarPinned, setActionBarPinned] = useState(false);
  const [isAnimationPaused, setIsAnimationPaused] = useState(false);
  const resetAnimationRef = useRef(null);
  const { user, isSignedIn, isLoaded } = useUser();
  const [showTeamResponsibilities, setShowTeamResponsibilities] = useState(false);
  const [automationPeople, setAutomationPeople] = useState({});
  
  const [notification, setNotification] = useState({
    showNotif: false,
    contents: {
      type: "",
      heading: "",
      message: "",
    },
  });

  // Add new state for bottom notification
  const [showBottomNotification, setShowBottomNotification] = useState(false);

  // Add state for video card visibility
  const [showVideoCard, setShowVideoCard] = useState(false);

  // Add state for Calendly widget visibility
  const [showCalendly, setShowCalendly] = useState(false);

  // Add state for automate form
  const [showAutomateForm, setShowAutomateForm] = useState(false);

  // Add state for notifications dropdown
  const [showNotificationsDropdown, setShowNotificationsDropdown] =
    useState(false);

  // Add state for integrations modal
  const [showIntegrationsModal, setShowIntegrationsModal] = useState(false);

  // Add this near your other state declarations
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Add phone number state
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [phoneMsg, setPhoneMsg] = useState("");
  const [showPhoneInput, setShowPhoneInput] = useState(true);
  
  // State to restart Typed animation on hover
  const [typedKey, setTypedKey] = useState(0);

  // Add filter state
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    customerFocus: ["B2B"], // Default to B2B checked
    industries: [],
    sizes: [],
    stages: [],
    automationStatus: [],
  });
  const [companyData, setCompanyData] = useState(null);

  // Field type definitions for validation
  const FIELD_TYPES = {
    Name: "string",
    Domain: "string",
    Industry: "string",
    Address: "string",
    ListSize: "number",
    AnnualRevenue: "number",
    OperationalCosts: "number",
    Debt: "number",
    ConversionRate: "number", // Decimal between 0-1
    FollowerCount: "number",
    AvgDealSize: "number",
    Technologies: "string",
    VisitToLeadConversionRate: "number",
    LeadToDealConversionRate: "number",
    IncreasedAnnualTraffic: "number",
  };

  // Handler to update company fields in Airtable
  const handleUpdateField = async (fieldName, value) => {
    if (!companyData?.id) {
      console.error("No company data available");
      setNotification({
        showNotif: true,
        contents: {
          title: "Error",
          message: "Company information not available",
          type: "error",
        },
      });
      return;
    }

    try {
      // Validate value based on field type
      const fieldType = FIELD_TYPES[fieldName];
      let processedValue = value;

      // Handle number fields - process both string and number types
      if (fieldType === "number") {
        // Convert string to number if needed
        if (typeof value === "string") {
          processedValue = Number(value.replace(/[^0-9.]/g, ""));
        } else {
          // Already a number, ensure it's valid
          processedValue = Number(value);
        }
        
        if (isNaN(processedValue)) {
          throw new Error("Invalid number format");
        }
        
        // For integer fields like ListSize, FollowerCount, convert to integer
        if (fieldName === "ListSize" || 
            fieldName === "FollowerCount" || 
            fieldName === "IncreasedAnnualTraffic") {
          processedValue = Math.round(processedValue);
        }
        
        // Ensure value is not negative for these fields
        if ((fieldName === "ListSize" || 
             fieldName === "FollowerCount" || 
             fieldName === "IncreasedAnnualTraffic") && 
            processedValue < 0) {
          processedValue = 0;
        }
      }

      debugLog(`Updating ${fieldName} to:`, processedValue);

      // Update the company record in Airtable
      await base("companies").update([
        {
          id: companyData.id,
          fields: {
            [fieldName]: processedValue,
          },
        },
      ]);

      // Update local state
      setCompanyData((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          [fieldName]: processedValue,
        },
      }));

      setNotification({
        showNotif: true,
        contents: {
          title: "Success",
          message: "Company information updated successfully",
          type: "success",
        },
      });
    } catch (error) {
      console.error("Error updating company field:", error);
      console.error("Field name:", fieldName, "Value:", value, "Type:", typeof value);
      setNotification({
        showNotif: true,
        contents: {
          title: "Error",
          message:
            error.message === "Invalid number format"
              ? "Please enter a valid number"
              : `Failed to update company information: ${error.message || error}`,
          type: "error",
        },
      });
    }
  };

  // Add clipboard icon state
  const [showClipboardList, setShowClipboardList] = useState(false);
  const [prioritizedIds, setPrioritizedIds] = useState([]);
  const [allCards, setAllCards] = useState([]);

  // Add category and stage state
  const [categories, setCategories] = useState([
    { id: "all", name: "All Categories" },
    { id: "Sales & Marketing", name: "Sales & Marketing" },
    { id: "Onboarding", name: "Onboarding" },
    { id: "Customer Success", name: "Customer Success" },
    { id: "Project Management", name: "Project Management" },
    { id: "HR & Recruiting", name: "HR & Recruiting" },
    { id: "Finance & Accounting", name: "Finance & Accounting" },
  ]);
  const [stages, setStages] = useState([
    { id: "all", name: "All" },
    { id: "my_list", name: "My List 📌" },
    { id: "recommended", name: "Recommended", deprecated: true },
    { id: "popular", name: "Popular" },
    { id: "ready", name: "Ready to Use" },
    { id: "coming_soon", name: "Coming Soon" },
    { id: "requested", name: "Requested" },
    { id: "value_menu", name: "Value Menu 🍔🍟" },
    { id: "value_rank", name: "Value Rank ✨📈" },
    { id: "start_grow", name: "Start & Grow 🚀💼" },
  ]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStage, setSelectedStage] = useState("all");
  const [showPrioritizedList, setShowPrioritizedList] = useState(false);

  useEffect(() => {
    if (!isSignedIn || !isLoaded) return;

    // Check if user's email domain matches the admin domain
    if (
      user?.primaryEmailAddress?.emailAddress?.split("@")[1] ===
      process.env.REACT_APP_COOL_EMAIL
    ) {
      setAdmin(true);
    }
  }, [user, isSignedIn, isLoaded]);

  // Load company data for filters
  useEffect(() => {
    if (company && !companyData) {
      setCompanyData(company);
    }
  }, [company, companyData]);

  // Initialize filters from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // Load category from URL
    const categoryParam = urlParams.get("category");
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }

    // Load stage from URL
    const stageParam = urlParams.get("stage");
    if (stageParam) {
      if (stageParam === "prioritized") {
        setShowPrioritizedList(true);
        setSelectedStage(null);
      } else if (["ready", "coming_soon", "requested"].includes(stageParam)) {
        // Convert old stage params to automationStatus filter
        setShowPrioritizedList(false);
        setSelectedStage("all");
        setFilters((prev) => ({ ...prev, automationStatus: [stageParam] }));
      } else {
        setShowPrioritizedList(false);
        setSelectedStage(stageParam);
      }
    }

    // Load automation status from URL
    const automationStatusParam = urlParams.get("automationStatus");
    if (automationStatusParam) {
      const automationStatusArray = automationStatusParam.split(",").filter(Boolean);
      setFilters((prev) => ({ ...prev, automationStatus: automationStatusArray }));
    }

    // Load customer focus from URL
    const customerFocusParam = urlParams.get("customerFocus");
    if (customerFocusParam) {
      const customerFocusArray = customerFocusParam.split(",").filter(Boolean);
      setFilters((prev) => ({ ...prev, customerFocus: customerFocusArray }));
    }

    // Load industries from URL
    const industriesParam = urlParams.get("industries");
    if (industriesParam) {
      const industriesArray = industriesParam.split(",").filter(Boolean);
      setFilters((prev) => ({ ...prev, industries: industriesArray }));
    }

    // Load sizes from URL
    const sizesParam = urlParams.get("sizes");
    if (sizesParam) {
      const sizesArray = sizesParam.split(",").filter(Boolean);
      setFilters((prev) => ({ ...prev, sizes: sizesArray }));
    }
  }, []);

  // Handle stage change function
  const handleStageChange = (stageId) => {
    setSelectedStage(stageId);
  };

  // Update URL when filters change
  const updateURL = () => {
    const url = new URL(window.location);
    const params = new URLSearchParams();

    // Add category
    if (selectedCategory && selectedCategory !== "all") {
      params.set("category", selectedCategory);
    }

    // Add stage
    if (showPrioritizedList) {
      params.set("stage", "prioritized");
    } else if (selectedStage && selectedStage !== "all") {
      params.set("stage", selectedStage);
    }

    // Add customer focus
    if (filters.customerFocus.length > 0) {
      params.set("customerFocus", filters.customerFocus.join(","));
    }

    // Add industries
    if (filters.industries.length > 0) {
      params.set("industries", filters.industries.join(","));
    }

    // Add sizes
    if (filters.sizes.length > 0) {
      params.set("sizes", filters.sizes.join(","));
    }

    // Add automation status
    if (filters.automationStatus && filters.automationStatus.length > 0) {
      params.set("automationStatus", filters.automationStatus.join(","));
    }

    // Update URL without page reload
    const newUrl = params.toString()
      ? `${url.pathname}?${params.toString()}`
      : url.pathname;
    window.history.replaceState({}, "", newUrl);
  };

  // Update URL when filters change
  useEffect(() => {
    updateURL();
  }, [selectedCategory, selectedStage, showPrioritizedList, filters]);

  // Update page title based on first industry
  useEffect(() => {
    const firstIndustry = filters.industries[0];
    if (firstIndustry) {
      document.title = `${firstIndustry} App Store for AI Work`;
    } else {
      document.title = "App Store for AI Work";
    }
  }, [filters.industries]);

  // Clear all filters function
  const clearAllFilters = () => {
    setSelectedCategory("all");
    setSelectedStage("all");
    setShowPrioritizedList(false);
    setFilters({
      customerFocus: [],
      industries: [],
      sizes: [],
      stages: [],
      automationStatus: [],
    });
    // Clear URL parameters
    window.history.replaceState({}, "", window.location.pathname);
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Phone call handler
  const handlePhoneCallTwo = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setPhoneMsg("");
    debugLog("[Bland] Starting phone call submission");

    // Format phone number (ensure it starts with +)
    let formattedPhone = phone.trim();
    if (!formattedPhone.startsWith("+")) {
      formattedPhone = "+1" + formattedPhone.replace(/[^0-9]/g, "");
    }
    debugLog("[Bland] Formatted phone number:", formattedPhone);

    const headers = {
      Authorization:
        "Bearer org_2bc8933c5047403e0800b9ab43ad22f894655b61edb36eb938c0b1d001f85b577dd0b12f8e441b04e34c69",
      "Content-Type": "application/json",
    };

    const data = {
      phone_number: formattedPhone,
      task: `Freedom Habits Coaching
Your name is Sarah, and you're a business performance and accountability coach from Freedom Habits Coaching working with entrepreneurs to double their focus and effectiveness. Every week you guide your client through a 30-minute "Review -> Clarify -> Commit" ritual: ...`, // Use your full prompt here
    };

    try {
      const response = await fetch("https://api.bland.ai/v1/calls", {
        method: "POST",
        headers,
        body: JSON.stringify(data),
      });
      const result = await response.json();
      debugLog("[Bland] Response JSON:", result);
      if (response.ok) {
        setPhoneMsg("AI call is on its way! Check your phone.");
      } else {
        setPhoneMsg("There was an error sending the call. Please try again.");
      }
    } catch (err) {
      console.error("[Bland] Error:", err);
      setPhoneMsg("Error sending call. See console for details.");
    } finally {
      setSubmitting(false);
      setPhone("");
    }
  };

  // Function to scroll to the bottom of the page
  const scrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (showCalendly) {
      const script = document.createElement("script");
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.async = true;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [showCalendly]);

  // Modify the VideoCard component to only render when showVideoCard is true
  const VideoCard = () =>
    showVideoCard && (
      <div className={`fixed left-2 transform max-w-md bg-white rounded-lg shadow-lg p-4 z-40 animate-fade-in min-w-[450px] ${
        embeddedInLayout ? "bottom-28 sm:bottom-24" : "bottom-2"
      }`}>
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1">
            <div className="pr-2">
              <h3 className="text-sm font-medium text-gray-900">
                Automate My Work Explained
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Watch these automations transform your company
              </p>
              <div className="mt-2">
                <div
                  style={{
                    position: "relative",
                    paddingBottom: "62.40601503759399%",
                    height: 0,
                  }}
                >
                  <iframe
                    src="https://www.loom.com/embed/9c9c062c1ca54f97899b69266bf700cf?sid=f9cec3f4-3494-4f60-a1c4-a094735b05de"
                    frameBorder="0"
                    webkitallowfullscreen="true"
                    mozallowfullscreen="true"
                    allowFullScreen
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="flex h-5 w-5 items-center justify-center rounded-full text-gray-400 hover:text-gray-500 focus:outline-none"
            onClick={() => setShowVideoCard(false)}
          >
            <span className="sr-only">Close</span>
            <svg
              className="h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    );

  const actionBarExpanded =
    isHoveringActionButtons ||
    actionBarPinned ||
    isFiltersOpen ||
    showNotificationsDropdown ||
    showClipboardList ||
    isMenuOpen ||
    showTeamResponsibilities;

  const getQueryVariable = (variable) => {
    const query = location.search.substring(1);
    const vars = query.split("&");
    for (let i = 0; i < vars.length; i++) {
      const pair = vars[i].split("=");
      if (pair[0] === variable) {
        return decodeURIComponent(pair[1]);
      }
    }
    return false;
  };

  const authRedirect = embeddedInLayout
    ? `/ai-automations${location.search || ""}`
    : `/automate-my-work${location.search || ""}`;

  return (
    <div
      className={`transition-colors duration-300 ${isDarkMode ? "bg-[rgb(16,16,16)]" : "bg-[#F5F0EA]"} ${
        embeddedInLayout ? "min-h-0 pb-8" : ""
      }`}
    >
      {/* Unified Action Buttons Bar - Collapsed under top-right FAB, expands on hover */}
      <div
        className={`fixed top-4 right-4 z-40 ${embeddedInLayout ? "lg:right-6" : ""}`}
        onMouseEnter={() => setIsHoveringActionButtons(true)}
        onMouseLeave={() => {
          setIsHoveringActionButtons(false);
          if (
            !isFiltersOpen &&
            !showNotificationsDropdown &&
            !showClipboardList &&
            !isMenuOpen &&
            !showTeamResponsibilities
          ) {
            setActionBarPinned(false);
          }
        }}
      >
        <div className="relative flex flex-col items-end">
          {/* Always-visible trigger circle */}
          <Tooltip text={actionBarExpanded ? "Hide tools" : "Tools"}>
            <button
              type="button"
              onClick={() => setActionBarPinned((prev) => !prev)}
              aria-expanded={actionBarExpanded}
              aria-label="Toggle action tools"
              className={`relative z-20 bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center ${
                actionBarExpanded ? "ring-2 ring-stone-600" : ""
              }`}
            >
              <EllipsisVerticalIcon
                className={`h-6 w-6 text-stone-600 transition-transform duration-300 ${
                  actionBarExpanded ? "rotate-90 scale-110" : "group-hover:scale-110"
                }`}
              />
            </button>
          </Tooltip>

          {/* Expanding stack — min-w keeps left tooltips inside the scroll box; height nearly fills viewport */}
          <div
            className={`absolute right-0 top-0 z-10 flex min-w-[14.5rem] flex-col items-end gap-3 overflow-y-auto overflow-x-hidden pt-[3.75rem] pb-2 transition-all duration-300 ease-out ${
              embeddedInLayout
                ? "max-h-[calc(100dvh-5.5rem)]"
                : "max-h-[calc(100dvh-1.25rem)]"
            } ${
              actionBarExpanded
                ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
                : "pointer-events-none -translate-y-2 scale-95 opacity-0"
            }`}
            style={{ scrollbarWidth: "thin" }}
          >
        {/* 1. Dark Mode Toggle */}
        <DarkModeToggle isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />

        {/* 2. Auth Buttons - Moved to top */}
        {isSignedIn ? (
          <>
            {/* Leaderboard Button */}
            <Tooltip text="Leaderboard">
              <a
                href="/public-leaderboard"
                className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center"
              >
                <TrophyIcon className="h-6 w-6 text-stone-600 group-hover:scale-110 transition-transform duration-200" />
              </a>
            </Tooltip>

            {/* My Dashboard Button */}
            <Tooltip text="My Dashboard">
              <a
                href={`/grants-section?website=${user?.primaryEmailAddress?.emailAddress?.split("@")[1] || ""}`}
                className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center relative"
              >
                {user?.imageUrl ? (
                  <img
                    src={user.imageUrl}
                    alt="Profile"
                    className="w-6 h-6 rounded-full group-hover:scale-110 transition-transform duration-200"
                  />
                ) : (
                  <UserIcon className="h-6 w-6 text-stone-600 group-hover:scale-110 transition-transform duration-200" />
                )}
              </a>
            </Tooltip>
          </>
        ) : (
          <>
            {/* Leaderboard Button */}
            <Tooltip text="Leaderboard">
              <a
                href="/public-leaderboard"
                className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center"
              >
                <TrophyIcon className="h-6 w-6 text-stone-600 group-hover:scale-110 transition-transform duration-200" />
              </a>
            </Tooltip>

            {/* Sign Up Button */}
            <Tooltip text="Get Started">
              <a
                href={`/sign-up?redirect=${encodeURIComponent(authRedirect)}`}
                className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center"
              >
                <SparklesIcon className="h-6 w-6 text-stone-600 group-hover:scale-110 transition-transform duration-200" />
              </a>
            </Tooltip>

            {/* Sign In Button */}
            <Tooltip text="Sign In">
              <a
                href={`/sign-in?redirect=${encodeURIComponent(authRedirect)}`}
                className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center"
              >
                <UserIcon className="h-6 w-6 text-stone-600 group-hover:scale-110 transition-transform duration-200" />
              </a>
            </Tooltip>
          </>
        )}

        {/* 3. Animation Pause/Play Button
        <Tooltip text={isAnimationPaused ? "Play animation" : "Pause animation"}>
          <button
            onClick={() => setIsAnimationPaused(!isAnimationPaused)}
            className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center"
          >
            {isAnimationPaused ? (
              <PlayIcon className="h-6 w-6 text-stone-600 group-hover:scale-110 transition-transform duration-200" />
            ) : (
              <PauseIcon className="h-6 w-6 text-stone-600 group-hover:scale-110 transition-transform duration-200" />
            )}
          </button>
        </Tooltip> */}

        {/* 3.5. Reset Animation Button */}
        <Tooltip text="Reset list animation">
          <button
            onClick={() => {
              if (resetAnimationRef.current) {
                resetAnimationRef.current();
              }
            }}
            className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center"
          >
            <ArrowPathIcon className="h-6 w-6 text-stone-600 group-hover:scale-110 transition-transform duration-200" />
          </button>
        </Tooltip>

        {/* 4. Guide/Play Button */}
        <Tooltip text="Watch Guide">
          <button
            onClick={() => setShowGuide(true)}
            className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center"
          >
            <PlayIcon className="h-6 w-6 text-stone-600 group-hover:scale-110 transition-transform duration-200" />
          </button>
        </Tooltip>

        {/* 5. Notifications Dropdown */}
        <NotificationsDropdown
          isOpen={showNotificationsDropdown}
          setIsOpen={setShowNotificationsDropdown}
          showVideoCard={showVideoCard}
          setShowVideoCard={setShowVideoCard}
          showBottomNotification={showBottomNotification}
          setShowBottomNotification={setShowBottomNotification}
          admin={admin}
        />

        {/* 6. Filter Button */}
        <Tooltip text="Filters">
          <div className="relative">
            <FilterButton
              isOpen={isFiltersOpen}
              setIsOpen={setIsFiltersOpen}
              companyData={companyData}
              onUpdateField={handleUpdateField}
              filters={filters}
              setFilters={setFilters}
              categories={categories}
              stages={stages}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              selectedStage={selectedStage}
              setSelectedStage={setSelectedStage}
              showPrioritizedList={showPrioritizedList}
              setShowPrioritizedList={setShowPrioritizedList}
              handleStageChange={handleStageChange}
              clearAllFilters={clearAllFilters}
            />
          </div>
        </Tooltip>

        {/* 7. Request AI Automation Button */}
        <Tooltip text="Request AI Automation">
          <button
            onClick={() => setShowAutomateForm(true)}
            className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center"
          >
            <PlusIcon className="h-6 w-6 text-stone-600 group-hover:scale-110 transition-transform duration-200" />
          </button>
        </Tooltip>

        {/* 8. Take a Tour Button */}
        <Tooltip text="Take a Tour">
          <button
            onClick={() => setRunTour(true)}
            className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center"
          >
            <MapIcon className="h-6 w-6 text-stone-600 group-hover:scale-110 transition-transform duration-200" />
          </button>
        </Tooltip>

        {/* 9. My Task/Clipboard List Button */}
        <div className="relative">
          <ClipboardList
            showClipboardList={showClipboardList}
            setShowClipboardList={setShowClipboardList}
            prioritizedIds={prioritizedIds}
            allCards={allCards}
            onRemove={(cardId) => {
              setPrioritizedIds(prev => prev.filter(id => id !== cardId));
            }}
          />
        </div>

        {/* 10. Integrations Button */}
        <Tooltip text="Integrations">
          <button
            onClick={() => setShowIntegrationsModal(true)}
            className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center"
          >
            <CubeTransparentIcon className="h-6 w-6 text-stone-600 group-hover:scale-110 transition-transform duration-200" />
          </button>
        </Tooltip>

        {/* 10.5 Team Responsibilities */}
        <Tooltip text="Team Responsibilities">
          <button
            type="button"
            onClick={() => setShowTeamResponsibilities(true)}
            className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center"
          >
            <UserGroupIcon className="h-6 w-6 text-stone-600 group-hover:scale-110 transition-transform duration-200" />
          </button>
        </Tooltip>

        {/* 11. Onboarding Button */}
        <Tooltip text="Onboarding">
          <Link
            to="/onboarding"
            className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center"
          >
            <FingerPrintIcon className="h-6 w-6 text-stone-600 group-hover:scale-110 transition-transform duration-200" />
          </Link>
        </Tooltip>

        {/* 12. Support/Help Button - Moved to bottom */}
        <div className="relative">
          {/* Menu items that slide down when expanded */}
          <div
            className={`absolute top-full mt-2 right-0 flex flex-col gap-2 transition-all duration-300 ${
              isMenuOpen
                ? "opacity-100 translate-y-0"
                : "opacity-0 -translate-y-4 pointer-events-none"
            }`}
          >
            {/* Submit Feedback & Feature Requests Button */}
            <Tooltip text="Submit Feedback & Feature Requests">
              <a
                href="https://app.asana.com/1/1204481977610635/project/1211334447615100/board"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center"
              >
                <svg
                  className="h-6 w-6 text-orange-500 group-hover:scale-110 transition-transform duration-200"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                  />
                </svg>
              </a>
            </Tooltip>

            {/* Slack Community Button */}
            <Tooltip text="Join our Slack community">
              <a
                href="https://upgraded-hq.slack.com/ssb/redirect?_gl=1*1r81mxn*_gcl_au*MTEzNjU2NzU1MS4xNzU5NDY5Njg5"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center"
              >
                <ChatBubbleLeftRightIcon className="h-6 w-6 text-[#4A154B] group-hover:scale-110 transition-transform duration-200" />
              </a>
            </Tooltip>

            {/* Need Help Button */}
            <Tooltip text="Need help? Schedule a call">
              <button
                onClick={() => setShowCalendly(true)}
                className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center"
              >
                <QuestionMarkCircleIcon className="h-6 w-6 text-blue-600 group-hover:scale-110 transition-transform duration-200" />
              </button>
            </Tooltip>
          </div>

          {/* Main toggle button */}
          <Tooltip text="Help & Support Resources">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group flex items-center justify-center ${
                isMenuOpen ? "ring-2 ring-stone-600" : ""
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-6 w-6 text-stone-600 transition-transform duration-300 ${
                  isMenuOpen ? "scale-110" : ""
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </button>
          </Tooltip>
        </div>
          </div>
        </div>
      </div>

      <div className="fixed top-4 right-4 z-50 pointer-events-none">
        <Notification
          showNotif={notification.showNotif}
          setShowNotif={(val) =>
            setNotification((prev) => ({ ...prev, showNotif: val }))
          }
          contents={notification.contents}
          zindex={50}
        />
      </div>

      <main className={`w-full max-w-6xl xl:max-w-7xl mx-auto max-lg:pb-16 px-4 sm:px-6 lg:px-8 py-8 md:py-12 space-y-4 flex flex-col items-stretch relative ${
        embeddedInLayout ? "min-h-0" : "min-h-screen"
      }`}>
        <Header
          runTour={runTour}
          setRunTour={setRunTour}
          admin={admin}
          setNotification={setNotification}
          isDarkMode={isDarkMode}
          showAutomateForm={showAutomateForm}
          setShowAutomateForm={setShowAutomateForm}
          showGuide={showGuide}
          setShowGuide={setShowGuide}
          typedKey={typedKey}
          setTypedKey={setTypedKey}
        />

        <div className="mt-6 w-full drop-shadow-sm">
          <AutomationDirectory
            company={company}
            runTour={runTour}
            setRunTour={setRunTour}
            isAdmin={admin}
            isDarkMode={isDarkMode}
            setNotification={setNotification}
            // Pass filter props
            isFiltersOpen={isFiltersOpen}
            setIsFiltersOpen={setIsFiltersOpen}
            filters={filters}
            setFilters={setFilters}
            companyData={companyData}
            setCompanyData={setCompanyData}
            // Pass category and stage props
            categories={categories}
            setCategories={setCategories}
            stages={stages}
            setStages={setStages}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            selectedStage={selectedStage}
            setSelectedStage={setSelectedStage}
            showPrioritizedList={showPrioritizedList}
            setShowPrioritizedList={setShowPrioritizedList}
            handleStageChange={handleStageChange}
            // Pass clipboard list props
            prioritizedIds={prioritizedIds}
            setPrioritizedIds={setPrioritizedIds}
            allCards={allCards}
            setAllCards={setAllCards}
            // Pass action buttons hover state
            isHoveringActionButtons={isHoveringActionButtons}
            // Pass animation pause state
            isAnimationPaused={isAnimationPaused}
            // Pass reset animation ref
            resetAnimationRef={resetAnimationRef}
          />
        </div>

      </main>

      {/* Scroll to top button and phone input */}
      {showScrollTop && (
        <div className={`fixed left-8 flex items-center gap-4 z-40 ${
          embeddedInLayout ? "bottom-28 sm:bottom-24" : "bottom-8"
        }`}>
          <button
            onClick={scrollToTop}
            className="p-2 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 ease-in-out group"
          >
            <img
              src="/images/cloud-up.png"
              alt="Scroll to top"
              className="h-6 w-6 group-hover:scale-110 transition-transform duration-200"
            />
          </button>

          {/* Phone number field for AI call demo */}
          {showPhoneInput && (
            <div className="bg-white rounded-lg shadow-lg p-3 max-w-lg relative">
              {/* Close button */}
              <button
                onClick={() => setShowPhoneInput(false)}
                className="absolute -top-2 -right-2 p-1 rounded-full hover:bg-gray-500 bg-gray-800 transition-colors duration-200"
                aria-label="Close"
              >
                <XMarkIcon className="h-4 w-4 text-white font-semibold" />
              </button>
              {/*
                We'll check if the phone number is 10 digits (ignoring non-digits).
                If so, make the button two shades darker (bg-stone-900, hover:bg-black).
              */}
              <form
                onSubmit={handlePhoneCallTwo}
                className="flex flex-row items-center gap-2"
              >
                <input
                  type="tel"
                  placeholder="Enter your phone number"
                  className="flex-1 text-xs border-none outline-none bg-transparent focus:outline-none focus:ring-0 pr-6"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  disabled={submitting}
                />
                {(() => {
                  const digits = phone.replace(/\D/g, "");
                  const isTenDigits = digits.length === 10;
                  const buttonBase = isTenDigits
                    ? "bg-stone-900 hover:bg-black"
                    : "bg-stone-700 hover:bg-stone-800";
                  return (
                    <button
                      type="submit"
                      className={`${buttonBase} text-white px-3 py-1.5 rounded text-xs font-medium flex-shrink-0 cursor-pointer`}
                      disabled={submitting || !phone}
                    >
                      {submitting ? "Calling..." : "Call Me"}
                    </button>
                  );
                })()}
              </form>
              <div className="text-xs text-gray-500 text-center mt-2 font-medium">
                Order AI via voice demo — no signup needed.
              </div>
              {phoneMsg && (
                <div className="text-xs text-green-600 text-center mt-1">
                  {phoneMsg}
                </div>
              )}
            </div>
          )}
        </div>
      )}


      {/* Keep your existing VideoCard and bottom notification components */}
      {showVideoCard && <VideoCard />}
      {showBottomNotification && (
        <div
          className="fixed left-2 transform max-w-md bg-white rounded-lg shadow-lg p-4 z-50 animate-fade-in transition-all duration-300"
          style={{ bottom: showVideoCard ? "335px" : "24px" }}
        >
          <div className="flex justify-between items-start gap-4">
            <div className="flex-1">
              <div className="pr-2">
                <h3 className="text-sm font-medium text-gray-900">
                  Unlimited Automation Requests
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Unlimited automation requests with 48-hour average turnaround
                  time (guide icon). Try risk-free, 30-day money-back guarantee.
                  <br />
                  <strong>Limited to 1 spot per month.</strong>
                  <br />
                  <strong> Or $225/hr for custom work.</strong>
                </p>
                <div className="mt-4">
                  <button
                    onClick={() => {
                      window.open(
                        "https://buy.stripe.com/6oE17O6NA1ukdag9AC",
                        "_blank"
                      );
                      setShowBottomNotification(false);
                    }}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-stone-600 hover:bg-stone-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stone-500"
                  >
                    Get Unlimited Access
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <img
                src="https://cdn.dribbble.com/users/458944/screenshots/5058685/30day-01_4x.png?resize=1000x750&vertical=center"
                alt="Automation illustration"
                className="w-[120px] h-[120px] object-cover rounded-lg"
              />
              <button
                onClick={() => setShowBottomNotification(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-4 max-w-4xl w-full max-h-[90vh] overflow-auto relative">
            <button
              onClick={() => setShowCalendly(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h2 className="text-xl font-bold mb-4">Schedule a Consultation</h2>

            {/* Updated Calendly widget with your specific URL */}
            <div
              className="calendly-inline-widget"
              data-url="https://calendly.com/willrichman/upgraded-ai-automations-office-hours"
              style={{ minWidth: "320px", height: "700px" }}
            ></div>
          </div>
        </div>
      )}

      {/* Add Calendly scripts */}
      {showCalendly && (
        <script
          type="text/javascript"
          src="https://assets.calendly.com/assets/external/widget.js"
          async
        ></script>
      )}

      {/* Automate Form Modal */}
      <AutomateForm isOpen={showAutomateForm} setIsOpen={setShowAutomateForm} />

      {/* Integrations Modal */}
      <IntegrationsModal 
        isOpen={showIntegrationsModal} 
        setIsOpen={setShowIntegrationsModal}
        user={user}
        setNotification={setNotification}
      />

      {/* Team Responsibilities — demoted from top tabs into FAB modal */}
      <Transition.Root show={showTeamResponsibilities} as={Fragment}>
        <Dialog as="div" className="relative z-[120]" onClose={setShowTeamResponsibilities}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/40 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-[120] overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-6">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-4 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full max-w-6xl transform overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl transition-all">
                  <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                    <div>
                      <Dialog.Title className="text-base font-semibold text-gray-900">
                        Team Responsibilities
                      </Dialog.Title>
                      <p className="mt-0.5 text-sm text-gray-500">
                        Map roles and hours to unlock labour savings via automation.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowTeamResponsibilities(false)}
                      className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="max-h-[80vh] overflow-y-auto p-4 sm:p-6">
                    {company ? (
                      <AutomationContext.Provider value={{ automationPeople, setAutomationPeople }}>
                        <Automation
                          company={company}
                          getQueryVariable={getQueryVariable}
                          grantWriters={[]}
                          fullBlur={false}
                          hideTabBar
                          forceTab="responsibilities"
                        />
                      </AutomationContext.Provider>
                    ) : (
                      <p className="py-10 text-center text-sm text-gray-500">
                        Load a company to manage team responsibilities.
                      </p>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}
